/**
 * Ayuda contextual de la aplicación: un texto breve por pantalla, escrito para
 * quien entra desde fuera y no conoce la herramienta.
 *
 * Las claves de AYUDA_POR_RUTA son patrones de ruta: 'encargos' para la tabla,
 * 'encargos/:id' para el detalle y '' para la página de inicio.
 */
export interface AyudaSeccion {
  titulo: string;
  intro: string;
  pasos: string[];
}

export const AYUDA_GENERICA: AyudaSeccion = {
  titulo: 'Ayuda',
  intro:
    'Esta pantalla todavía no tiene instrucciones propias. Desde la barra superior puedes moverte entre las secciones de tu espacio de trabajo.',
  pasos: [
    'Usa el menú de la barra superior para cambiar de sección.',
    'Casi todas las pantallas tienen un campo "Buscar" que filtra lo que ves.',
    'Si algo no te cuadra, escríbenos y lo revisamos contigo.',
  ],
};

export const AYUDA_POR_RUTA: Record<string, AyudaSeccion> = {
  '': {
    titulo: 'Inicio',
    intro:
      'Es el resumen de tu espacio de trabajo. Cada tarjeta cuenta cuántos elementos tienes en una sección y te lleva a ella.',
    pasos: [
      'Pulsa cualquier tarjeta para abrir esa sección.',
      'Las cifras cuentan solo lo que está asociado a tus entidades y encargos.',
      'Si echas en falta algo que esperabas ver, avísanos: suele ser una cuestión de permisos.',
    ],
  },

  mapa: {
    titulo: 'Mapa',
    intro:
      'Sitúa tus encargos sobre el mapa y permite consultar información urbanística y catastral de cualquier punto.',
    pasos: [
      'Los marcadores son encargos con ubicación: pulsa uno para ver de cuál se trata.',
      'Haz doble clic en cualquier punto para consultar Catastro y el planeamiento urbanístico (MUIB) de esa coordenada.',
      'En "Servicios de Información Externos" puedes activar y desactivar las capas que se dibujan encima.',
      'Las consultas salen de tu navegador contra los servidores oficiales, así que pueden tardar unos segundos.',
    ],
  },

  entidades: {
    titulo: 'Entidades',
    intro:
      'Las entidades son las empresas y personas titulares de los encargos. Aquí están las que te corresponden.',
    pasos: [
      'Escribe en "Buscar" para filtrar por nombre, CIF/NIF, dirección, localidad o municipio.',
      'Pulsa una fila para abrir su ficha completa.',
    ],
  },

  'entidades/:id': {
    titulo: 'Ficha de entidad',
    intro:
      'Reúne los datos de la entidad y lo que cuelga de ella: encargos, actores y documentación relacionada.',
    pasos: [
      'Los iconos de copiar junto al nombre, CIF/NIF, dirección, teléfono y email llevan el dato al portapapeles.',
      'Desde la lista de encargos de la ficha llegas al detalle de cada uno.',
    ],
  },

  actores: {
    titulo: 'Actores',
    intro:
      'Los actores son las personas de contacto vinculadas a las entidades: quién interviene y cómo localizarle.',
    pasos: [
      'Escribe en "Buscar" para filtrar por nombre, apellidos, teléfono o entidad.',
      'Los iconos junto al teléfono y al email copian el dato al portapapeles.',
    ],
  },

  encargos: {
    titulo: 'Encargos',
    intro:
      'Cada encargo es un trabajo con su código, su titular y su estado. Esta es la vista general de los tuyos.',
    pasos: [
      'El desplegable "Año" limita la lista al ejercicio que elijas.',
      'Escribe en "Buscar" para filtrar por código, descripción, titular o entidad.',
      'Pulsa una fila para abrir el detalle del encargo.',
    ],
  },

  'encargos/:id': {
    titulo: 'Detalle del encargo',
    intro:
      'Toda la información de un encargo: datos, situación económica y lo que se le ha ido asociando.',
    pasos: [
      'Presupuestado, Facturado y Pendiente resumen el estado económico del encargo.',
      'Los iconos de copiar duplican el nombre, la dirección o la ubicación; uno de ellos copia el enlace directo a este encargo para compartirlo.',
      'Desde aquí llegas a sus documentos, fotos, comentarios y facturas.',
    ],
  },

  documentos: {
    titulo: 'Documentos',
    intro:
      'El archivo de documentación de tus encargos y entidades, con vista previa sin salir de la pantalla.',
    pasos: [
      'Los desplegables "Tipo" y "Área" acotan la lista; "Buscar" filtra por nombre.',
      'El icono de la lupa amplía el documento; el de la flecha lo descarga.',
      '"Ver encargo / entidad" te lleva al expediente del que cuelga el documento.',
      'Desde la ficha de un encargo puedes descargar de una vez todos sus documentos en un zip.',
    ],
  },

  comments: {
    titulo: 'Comentarios',
    intro:
      'Las anotaciones hechas sobre los encargos, en orden, para seguir el hilo de lo que se ha ido hablando.',
    pasos: [
      'Escribe en "Buscar" para filtrar por texto o por encargo.',
      'El código del proyecto que acompaña a cada comentario te lleva a su encargo.',
    ],
  },

  facturas: {
    titulo: 'Facturas',
    intro:
      'Tus facturas y proformas, con su número, su fecha y su estado. Las proformas son el documento previo al cobro; las liquidadas son la factura definitiva.',
    pasos: [
      'El desplegable "Año" limita la lista al ejercicio que elijas.',
      'El icono de descarga te da el PDF de la factura, con su código QR tributario si ya está liquidada.',
      'Una factura marcada como anulada en AEAT no tiene efecto fiscal, aunque siga apareciendo en la lista.',
    ],
  },

  fotos: {
    titulo: 'Fotos',
    intro:
      'La galería de imágenes asociadas a los encargos: visitas, estado de las obras y cualquier material gráfico.',
    pasos: [
      'Pulsa una foto para verla a tamaño completo.',
      '"Ver presentación" las pasa una a una; "Tamaño de las fotos" cambia cuántas caben en pantalla.',
      'El icono de descarga guarda la imagen original en tu equipo.',
      '"Ver encargo" te lleva al expediente al que pertenece la foto.',
    ],
  },
};

/**
 * Resuelve la ayuda que corresponde a una URL del router. Prueba primero la
 * clave de detalle ('encargos/:id') y luego la de la sección, de modo que una
 * pantalla de detalle sin texto propio hereda el de su tabla.
 */
export function ayudaParaUrl(url: string): AyudaSeccion {
  const ruta = (url || '').split('?')[0].split('#')[0].replace(/^\/+|\/+$/g, '');
  if (!ruta) return AYUDA_POR_RUTA[''];

  const segmentos = ruta.split('/');
  const claves = segmentos.length > 1 ? [`${segmentos[0]}/:id`, segmentos[0]] : [segmentos[0]];

  for (const clave of claves) {
    const seccion = AYUDA_POR_RUTA[clave];
    if (seccion) return seccion;
  }

  return AYUDA_GENERICA;
}
